import React from 'react';
import styled from '@emotion/styled';
import { GraphView } from '../components/GraphView';
import { useNavigate } from 'react-router-dom';

const Container = styled.div`
  width: 100%;
  height: 100vh;
  background: var(--color-background);
  position: relative;
`;

const Header = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;
  
  @media (max-width: 768px) {
    top: 16px;
    left: 16px;
    right: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const BackButton = styled.button`
  padding: 12px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  min-height: 44px; /* Accessibility minimum touch target */
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: var(--font-weight-medium);
  
  &:hover {
    background: var(--color-surface-hover);
    border-color: var(--color-primary);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 768px) {
    padding: 16px 24px;
    min-height: 56px; /* Larger touch target on mobile */
    font-size: 16px;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    
    &:hover {
      transform: none; /* Disable hover effects on touch devices */
    }
    
    &:active {
      background: var(--color-surface-hover);
      border-color: var(--color-primary);
      transform: scale(0.96);
    }
  }
  
  /* Better touch feedback for mobile */
  @media (hover: none) {
    &:hover {
      background: var(--color-surface);
      border-color: var(--color-border);
    }
  }
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
  margin-top: 12px;
  color: var(--color-text);
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-top: 0;
    text-align: center;
  }
`;

export const GraphPage: React.FC = () => {
  const navigate = useNavigate();
  
  const handleNodeClick = (nodeId: string, nodeData: any) => {
    // If it's a query node, navigate to search with that query
    if (nodeId.startsWith('query-')) {
      const query = nodeId.replace('query-', '');
      navigate(`/?q=${encodeURIComponent(query)}`);
    } else if (nodeId.startsWith('concept-')) {
      // For concept nodes, use the label as the query
      const query = nodeData?.label || nodeId.replace('concept-', '');
      navigate(`/?q=${encodeURIComponent(query)}`);
    }
  };
  
  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          ← Back to Search
        </BackButton>
        <Title>Your Knowledge Graph</Title>
      </Header>
      <GraphView onNodeClick={handleNodeClick} />
    </Container>
  );
};